import React from 'react';
import './Login.css';
import { Navbar } from '../components/Navbar';
import { sha256 } from 'js-sha256';
import { Redirect, Link } from "react-router-dom";
import { ClompApi } from '../api/ClompApi';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { GOOGLE_CLIENT_ID } from '../config';
import { endSession, takeLoginExpired } from '../api/session';

const api = new ClompApi();

export class Login extends React.Component {

    state = {
        username: "",
        password: "",
        // "Keep me logged in": the login then lasts 30 days from its last use instead of an hour
        remember: false,
        error: "",
        loggingIn: false,
        loggedIn: false,
        // The username of a login that is still good, once the backend has confirmed it
        alreadyLoggedInAs: null,
        // A login that ran out, here or on the page that sent the visitor here
        expired: takeLoginExpired()
    };

    componentDidMount() {
        // A token in the browser doesn't mean the login is still good: ask the backend
        if (localStorage.getItem('token') === null) {
            return;
        }
        this.mounted = true;
        api.auth.getSession().then((session) => {
            if (!this.mounted) {
                return;
            }
            if (session.loggedIn) {
                this.setState({ alreadyLoggedInAs: session.username });
            } else {
                endSession(false);
                this.setState({ expired: true });
            }
        }).catch(() => {});
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    onLogin = async (event) => {
        event.preventDefault();
        if (!this.state.username || !this.state.password) {
            this.setState({ error: "Enter your username and password." });
            return;
        }

        this.setState({ loggingIn: true, error: "" });
        try {
            let response = await api.auth.login(this.state.username, sha256(this.state.password), this.state.remember);
            if (response.success === false) {
                this.setState({ error: response.message || "Couldn't log in.", password: "", loggingIn: false });
            } else {
                localStorage.setItem('token', response.token);
                this.setState({ loggedIn: true });
            }
        } catch (err) {
            console.log(err);
            this.setState({ error: "Couldn't reach the server. Please try again.", loggingIn: false });
        }
    }

    onGoogleCredential = async (credential) => {
        this.setState({ loggingIn: true, error: "" });
        try {
            let response = await api.auth.googleLogin(credential, this.state.remember);
            localStorage.setItem('token', response.token);
            this.setState({ loggedIn: true });
        } catch (err) {
            console.log(err);
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({ error: message || "Couldn't sign in with Google. Please try again.", loggingIn: false });
        }
    }

    // Where to go after logging in: the page named by ?next= (only ever a path on this site), or home
    afterLogin() {
        let next = new URLSearchParams(this.props.location.search).get('next') || '';
        return next.startsWith('/') && !next.startsWith('//') ? next : '/home';
    }

    render() {
        if (this.state.loggedIn) {
            return <Redirect to={this.afterLogin()} />;
        }

        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className="login-page">
                    <div className="login-card">
                        <h1>Log in</h1>
                        <p className="login-intro">Welcome back to clomp.</p>

                        {this.state.expired ?
                            <p className="login-notice login-notice-expired" role="status">Your login has expired. Please log in again.</p>
                            : this.state.alreadyLoggedInAs ?
                            <p className="login-notice">You're already logged in as {this.state.alreadyLoggedInAs}. Logging in again will switch accounts.</p>
                            : null}

                        <form className="login-form" onSubmit={this.onLogin} noValidate>
                            <label htmlFor="login-username">Username</label>
                            <input id="login-username" name="username" type="text" autoComplete="username" value={this.state.username} onChange={(e) => this.setState({ username: e.target.value })} />

                            <label htmlFor="login-password">Password</label>
                            <input id="login-password" name="password" type="password" autoComplete="current-password" value={this.state.password} onChange={(e) => this.setState({ password: e.target.value })} />

                            <label className="login-remember">
                                <input type="checkbox" checked={this.state.remember} onChange={(e) => this.setState({ remember: e.target.checked })} />
                                Keep me logged in
                            </label>

                            {this.state.error ? <p className="login-error" role="alert">{this.state.error}</p> : null}

                            <button type="submit" className="login-btn login-btn-primary" disabled={this.state.loggingIn}>
                                {this.state.loggingIn ? "Logging in..." : "Log in"}
                            </button>
                        </form>

                        {GOOGLE_CLIENT_ID ?
                            <>
                                <div className="login-divider"><span>or</span></div>
                                <GoogleSignInButton onCredential={this.onGoogleCredential} onError={() => this.setState({ error: "Couldn't load Google sign-in." })} />
                            </>
                            : null}

                        <div className="login-divider"><span>New to clomp?</span></div>
                        <Link className="login-btn login-btn-ghost" to="/register">Create an account</Link>
                    </div>

                    <Link className="login-guest" to="/">Continue as guest</Link>
                </div>
            </>
        );

    }

}
